import { forwardRef, useCallback, useEffect, useMemo, useRef, useState, type ReactNode, type MutableRefObject } from 'react';
import { Group, Rect, Line, Text } from 'react-konva';
import Konva from 'konva';
import { toast } from 'sonner';
import { Node } from '../../../../core/models/scene';
import { useTableCellStore, type TableCellKey } from '../../../../stores/tableCellStore';
import { useDiagramStore } from '../../../../stores/diagramStore';
import type { TableCellStyle, TableCellStyleMap, TableCellTextMap } from '../../models/table/tableCell.types';
import { computeDockedLayoutUpdatesForTable } from '../../shapes/table/tableDockLayout.utils';
import { getTableCellRectByKey } from '../../shapes/table/tableDrop.utils';
import { resolveCellLayoutConfig } from '../../shapes/table/tableCellLayout.registry';
import { getTableCellTextRect } from '../../shapes/table/tableCellSpacing.utils';
import { getCommandManager } from '../../../../hooks/useCommandManager';
import { ReorderTableAxisCommand, isTableAxisReorderMergeSafe } from '../../../../core/commands/ReorderTableAxisCommand';
import { ResizeTableGridCommand } from '../../../../core/commands/ResizeTableGridCommand';
import { SessionMetrics } from '../../../../domains/analytics';
import { TABLE_SESSION_METRICS } from '../../../../domains/analytics/constants/MetricEvents';

interface TableShapeNodeProps {
  node: Node;
  selected: boolean;
  onDragStart?: (pos: { x: number; y: number }) => void;
  onDragMove?: (pos: { x: number; y: number }) => void;
  onDragEnd?: (pos: { x: number; y: number }) => void;
  onClick?: (e: any) => void;
  onStartCellTextEdit?: (cellKey: TableCellKey, textNode: Konva.Text) => void;
  editingCellKey?: TableCellKey | null;
  onTransformStart?: (e: any) => void;
  onTransformEnd?: (e: any) => void;
  onTransform?: (e: any) => void;
  canWrite?: boolean;
}

const clampCount = (value: unknown, fallback: number, min = 1): number => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return Math.max(min, Math.floor(value));
  }
  return fallback;
};

const MIN_CELL = 20;

type TableDock = { tableId: string; cellKey: `${number},${number}`; offset?: { x: number; y: number } };

type MergedRegion = { startRow: number; startCol: number; endRow: number; endCol: number };

const containsCell = (r: MergedRegion, row: number, col: number) => {
  return row >= r.startRow && row <= r.endRow && col >= r.startCol && col <= r.endCol;
};

const isMergedAnchor = (mr: MergedRegion, row: number, col: number) => {
  return mr.startRow === row && mr.startCol === col;
};

export const TableShapeNode = forwardRef<Konva.Group, TableShapeNodeProps>(({
  node,
  selected,
  onDragStart,
  onDragMove,
  onDragEnd,
  onClick,
  onStartCellTextEdit,
  editingCellKey,
  onTransformStart,
  onTransformEnd,
  onTransform,
  canWrite = true,
}, ref) => {
  const nodeIdSafe = useMemo(() => {
    const raw = String((node as any).id ?? '');
    return raw.replace(/[^a-zA-Z0-9_-]/g, '_');
  }, [node.id]);
  const setStageCursor = useCallback((cursor: string) => {
    const hasActiveCommentCursor = () => {
      try {
        const stage = groupRef.current?.getStage();
        const container = stage?.container();
        if (!container) return false;
        if (container.getAttribute('data-comment-cursor') === '1') return true;
        if (container.getAttribute('data-drawing-cursor') === '1') return true;
        if (container.getAttribute('data-text-cursor') === '1') return true;
        return false;
      } catch {
        return false;
      }
    };
    if (hasActiveCommentCursor()) {
      return;
    }
    try {
      const stage = groupRef.current?.getStage();
      const container = stage?.container();
      if (container) {
        container.style.cursor = cursor;
        return;
      }
    } catch {}
    try {
      document.body.style.cursor = cursor;
    } catch {}
  }, []);

  const rowResizeCursor = 'ns-resize';
  const colResizeCursor = 'ew-resize';

  const width = (node.width ?? node.w ?? 240) as number;
  const height = (node.height ?? node.h ?? 180) as number;
  const attrs = (node.attrs as Record<string, unknown>) ?? {};
  const cellTextMap = (attrs.cellText as TableCellTextMap | undefined) ?? {};
  const cellStyleMap = (attrs.cellStyles as TableCellStyleMap | undefined) ?? {};
  const mergedRegions = (Array.isArray((attrs as any).mergedRegions) ? ((attrs as any).mergedRegions as MergedRegion[]) : []) ?? [];

  const rowCount = clampCount(attrs.rowCount, 3);
  const columnCount = clampCount(attrs.columnCount, 3);
  const showHeader = attrs.showHeader !== false;
  const showHeaderColumn = (attrs as any).showHeaderColumn === true;
  const headerRowIndices = useMemo(() => {
    const raw = (attrs as any).headerRowIndices;
    if (!Array.isArray(raw)) return showHeader ? [0] : [];
    return (raw as unknown[]).map((n) => Number(n)).filter((n) => Number.isFinite(n));
  }, [attrs, showHeader]);
  const headerColIndices = useMemo(() => {
    const raw = (attrs as any).headerColIndices;
    if (!Array.isArray(raw)) return showHeaderColumn ? [0] : [];
    return (raw as unknown[]).map((n) => Number(n)).filter((n) => Number.isFinite(n));
  }, [attrs, showHeaderColumn]);
  const headerFillColor = (attrs.headerFillColor as string) || '#F1F5F9';
  const cellFillColor = (node.fillColor as string) || (attrs.cellFillColor as string) || '#FFFFFF';
  const gridColor = (node.strokeColor as string) || (attrs.gridColor as string) || '#97a3b6';
  const strokeWidth =
    typeof node.strokeWidth === 'number'
      ? node.strokeWidth
      : typeof attrs.strokeWidth === 'number'
        ? (attrs.strokeWidth as number)
        : 1;
  const dash = Array.isArray((attrs as any).dash) ? ((attrs as any).dash as number[]) : [];
  const dashEnabled = !!(attrs as any).dashEnabled && dash.length > 0;
  const snapToGrid = 12;
  const textColor = (attrs.textColor as string) || '#0F172A';
  const label = typeof node.text === 'string' ? node.text : (attrs.label as string);
  const headerHeightFixed = showHeader ? Math.max(MIN_CELL, (attrs.headerHeight as number) ?? 32) : 0;
  const bodyHeightBase = Math.max(MIN_CELL * rowCount, height - headerHeightFixed);
  const normalize = (arr: number[], total: number, fallbackCount: number) => {
    const sum = arr.reduce((s, n) => s + n, 0);
    if (!Number.isFinite(sum) || sum <= 0) {
      const even = total / fallbackCount;
      return Array(fallbackCount).fill(even);
    }
    const scale = total / sum;
    return arr.map((n) => Math.max(MIN_CELL, n * scale));
  };
  const rowHeights = normalize(
    (attrs.rowHeights as number[] | undefined)?.length === rowCount
      ? (attrs.rowHeights as number[])
      : Array(rowCount).fill(bodyHeightBase / rowCount),
    bodyHeightBase,
    rowCount,
  );
  const columnWidths = normalize(
    (attrs.columnWidths as number[] | undefined)?.length === columnCount
      ? (attrs.columnWidths as number[])
      : Array(columnCount).fill(width / columnCount),
    width,
    columnCount,
  );

  const combinedRowHeights = showHeader ? [headerHeightFixed, ...rowHeights] : [...rowHeights];

  const groupRef = useRef<Konva.Group | null>(null);
  const [hoveredSeparator, setHoveredSeparator] = useState<{ type: 'row' | 'col'; index: number } | null>(null);
  const rowDragRef = useRef<{ index: number; startY: number; baseHeights: number[] } | null>(null);
  const colDragRef = useRef<{ index: number; startX: number; baseWidths: number[] } | null>(null);
  const [isResizing, setIsResizing] = useState(false);
  const [draftCombinedHeights, setDraftCombinedHeights] = useState<number[] | null>(null);
  const [draftColWidths, setDraftColWidths] = useState<number[] | null>(null);
  const dockedResizeIdsRef = useRef<string[]>([]);
  const resizePreviewRafRef = useRef<number | null>(null);

  const effectiveCombinedHeights = draftCombinedHeights ?? combinedRowHeights;
  const effectiveColWidths = draftColWidths ?? columnWidths;

  const renderWidth = effectiveColWidths.reduce((s, n) => s + n, 0);
  const renderHeaderHeight = showHeader ? effectiveCombinedHeights[0] : 0;
  const renderHeight = effectiveCombinedHeights.reduce((s, n) => s + n, 0);

  const selection = useTableCellStore((s) => s.selections[node.id]);
  const setSelection = useTableCellStore((s) => s.setSelection);
  const clearSelection = useTableCellStore((s) => s.clearSelection);
  const [hoveredCellKey, setHoveredCellKey] = useState<TableCellKey | null>(null);
  const [reorderDrag, setReorderDrag] = useState<{
    axis: 'row' | 'col';
    fromIndex: number;
    insertionIndex: number;
    pos: number;
    dragPos: number;
  } | null>(null);
  const [isReordering, setIsReordering] = useState(false);
  const cellTextNodeRefs = useRef(new Map<TableCellKey, Konva.Text>());
  const cellTextRefCbMap = useRef(new Map<TableCellKey, (n: Konva.Text | null) => void>());

  useEffect(() => {
    if (!isReordering) return;
    const stage = groupRef.current?.getStage();
    if (!stage) return;
    const cleanup = () => {
      setReorderDrag(null);
      setIsReordering(false);
      setStageCursor('default');
    };
    stage.on('mouseup touchend', cleanup);
    return () => {
      stage.off('mouseup touchend', cleanup);
    };
  }, [isReordering]);

  const cellKey = (r: number, c: number) => `${r},${c}` as TableCellKey;
  const parseCellKey = (key: TableCellKey) => {
    const [rStr, cStr] = key.split(',');
    const r = Number(rStr);
    const c = Number(cStr);
    return { r, c };
  };

  const getCellTextRefCb = useCallback((key: TableCellKey) => {
    const existing = cellTextRefCbMap.current.get(key);
    if (existing) return existing;
    const cb = (n: Konva.Text | null) => {
      if (n) cellTextNodeRefs.current.set(key, n);
      else cellTextNodeRefs.current.delete(key);
    };
    cellTextRefCbMap.current.set(key, cb);
    return cb;
  }, []);

  const rowStarts = useMemo(() => {
    const acc: number[] = [0];
    for (let i = 0; i < effectiveCombinedHeights.length; i += 1) {
      acc.push(acc[i] + effectiveCombinedHeights[i]);
    }
    return acc;
  }, [effectiveCombinedHeights]);

  const colStarts = useMemo(() => {
    const acc: number[] = [0];
    for (let i = 0; i < effectiveColWidths.length; i += 1) {
      acc.push(acc[i] + effectiveColWidths[i]);
    }
    return acc;
  }, [effectiveColWidths]);

  const activeCellKeyForReorder =
    selection?.anchorKey ?? selection?.selectedKeys?.[0] ?? hoveredCellKey ?? null;
  const activeCellParsedForReorder = activeCellKeyForReorder ? parseCellKey(activeCellKeyForReorder) : null;
  const selectedIdsForReorder = useDiagramStore((s) => s.selectedIds);
  const canShowReorderHandles = canWrite && !isResizing && !!activeCellParsedForReorder;
  const canReorder = canShowReorderHandles && selectedIdsForReorder.size === 1 && selectedIdsForReorder.has(node.id);

  const getRowInsertionIndex = useCallback((y: number) => {
    const segments = effectiveCombinedHeights.length;
    if (segments <= 0) return 0;
    if (y <= 0) return 0;
    if (y >= renderHeight) return segments;
    for (let i = 0; i < segments; i += 1) {
      const y2 = rowStarts[i + 1] ?? renderHeight;
      if (y < y2) {
        const y1 = rowStarts[i] ?? 0;
        const mid = (y1 + y2) / 2;
        return y < mid ? i : i + 1;
      }
    }
    return segments;
  }, [effectiveCombinedHeights.length, renderHeight, rowStarts]);

  const getColInsertionIndex = useCallback((x: number) => {
    const segments = columnCount;
    if (segments <= 0) return 0;
    if (x <= 0) return 0;
    if (x >= renderWidth) return segments;
    for (let i = 0; i < segments; i += 1) {
      const x2 = colStarts[i + 1] ?? renderWidth;
      if (x < x2) {
        const x1 = colStarts[i] ?? 0;
        const mid = (x1 + x2) / 2;
        return x < mid ? i : i + 1;
      }
    }
    return segments;
  }, [colStarts, columnCount, renderWidth]);

  const findMergedRegion = useCallback((row: number, col: number): MergedRegion | null => {
    for (const r of mergedRegions) {
      if (
        typeof r?.startRow !== 'number' ||
        typeof r?.startCol !== 'number' ||
        typeof r?.endRow !== 'number' ||
        typeof r?.endCol !== 'number'
      ) {
        continue;
      }
      if (containsCell(r, row, col)) return r;
    }
    return null;
  }, [mergedRegions]);


  const normalizeHitToMergedAnchor = useCallback((hit: { r: number; c: number }) => {
    const mr = findMergedRegion(hit.r, hit.c);
    if (!mr) return hit;
    return { r: mr.startRow, c: mr.startCol };
  }, [findMergedRegion]);

  const getCellAtPoint = (pt: { x: number; y: number }) => {
    if (!Number.isFinite(pt.x) || !Number.isFinite(pt.y)) return null;
    if (pt.x < 0 || pt.y < 0) return null;
    const rows = effectiveCombinedHeights.length;
    const cols = columnCount;
    if (rows <= 0 || cols <= 0) return null;

    const x = pt.x >= renderWidth ? renderWidth - 0.0001 : pt.x;
    const y = pt.y >= renderHeight ? renderHeight - 0.0001 : pt.y;
    if (x < 0 || y < 0) return null;

    let c = cols - 1;
    for (let i = 0; i < cols; i += 1) {
      if (x < colStarts[i + 1]) {
        c = i;
        break;
      }
    }
    let r = rows - 1;
    for (let i = 0; i < rows; i += 1) {
      if (y < rowStarts[i + 1]) {
        r = i;
        break;
      }
    }
    return normalizeHitToMergedAnchor({ r, c });
  };

  const handleGroupMouseMove = () => {
    if (isResizing) return;
    const rel = groupRef.current?.getRelativePointerPosition();
    if (!rel) return;
    const cell = getCellAtPoint(rel);
    if (!cell) {
      if (hoveredCellKey) setHoveredCellKey(null);
      return;
    }
    const nextKey = cellKey(cell.r, cell.c);
    if (nextKey !== hoveredCellKey) setHoveredCellKey(nextKey);
  };

  const handleGroupMouseLeave = () => {
    if (hoveredCellKey) setHoveredCellKey(null);
  };

  const handleGroupClick = (e: any) => {
    try {
      const stage = groupRef.current?.getStage();
      const container = stage?.container();
      if (container?.getAttribute('data-comment-cursor') === '1') {
        return;
      }
    } catch {}
    if (isResizing) {
      onClick?.(e);
      return;
    }
    // First click should select the table only. Only when the table is already selected
    // do we manage cell selection.
    if (!selected) {
      if (selection?.selectedKeys?.length) {
        clearSelection(node.id);
      }
      onClick?.(e);
      return;
    }
    const rel = groupRef.current?.getRelativePointerPosition();
    if (!rel) {
      onClick?.(e);
      return;
    }
    const hit = getCellAtPoint(rel);
    if (!hit) {
      onClick?.(e);
      return;
    }
    const key = cellKey(hit.r, hit.c);
    const metaKey = !!e?.evt?.metaKey;
    const ctrlKey = !!e?.evt?.ctrlKey;
    const shiftKey = !!e?.evt?.shiftKey;
    const toggle = metaKey || ctrlKey;

    const existing = selection?.selectedKeys ?? [];
    const anchorKey = selection?.anchorKey;
    const existingSet = new Set<TableCellKey>(existing);

    if (shiftKey && anchorKey) {
      const a = parseCellKey(anchorKey);
      const rMin = Math.min(a.r, hit.r);
      const rMax = Math.max(a.r, hit.r);
      const cMin = Math.min(a.c, hit.c);
      const cMax = Math.max(a.c, hit.c);
      const nextRange: TableCellKey[] = [];
      for (let r = rMin; r <= rMax; r += 1) {
        for (let c = cMin; c <= cMax; c += 1) {
          nextRange.push(cellKey(r, c));
        }
      }
      const next = toggle ? Array.from(new Set<TableCellKey>([...existing, ...nextRange])) : nextRange;
      setSelection(node.id, { anchorKey, selectedKeys: next });
      if (next.length > 1) {
        SessionMetrics.increment(TABLE_SESSION_METRICS.MULTI_SELECTIONS);
      }
      if (selected) {
        e.cancelBubble = true;
        return;
      }
      onClick?.(e);
      return;
    }

    if (toggle) {
      if (existingSet.has(key)) existingSet.delete(key);
      else existingSet.add(key);
      const next = Array.from(existingSet);
      const finalKeys = next.length > 0 ? next : [key];
      setSelection(node.id, { anchorKey: anchorKey ?? key, selectedKeys: finalKeys });
      if (finalKeys.length > 1) {
        SessionMetrics.increment(TABLE_SESSION_METRICS.MULTI_SELECTIONS);
      }
      if (selected) {
        e.cancelBubble = true;
        return;
      }
      onClick?.(e);
      return;
    }

    if (existing.length === 1 && existing[0] === key) {
      clearSelection(node.id);
      onClick?.(e);
      return;
    }

    setSelection(node.id, { anchorKey: key, selectedKeys: [key] });
    onClick?.(e);
  };

  const handleGroupDoubleClick = (e: any) => {
    try {
      const stage = groupRef.current?.getStage();
      const container = stage?.container();
      if (container?.getAttribute('data-comment-cursor') === '1') {
        return;
      }
    } catch {}
    if (isResizing) return;
    if (!canWrite) return;
    const rel = groupRef.current?.getRelativePointerPosition();
    if (!rel) return;
    const hit = getCellAtPoint(rel);
    if (!hit) return;
    const key = cellKey(hit.r, hit.c);
    const textNode = cellTextNodeRefs.current.get(key);
    if (!textNode) return;
    e.cancelBubble = true;
    onStartCellTextEdit?.(key, textNode);
  };

  const setCombinedRef = useCallback((n: Konva.Group | null) => {
    groupRef.current = n;
    if (!ref) return;
    if (typeof ref === 'function') {
      ref(n);
      return;
    }
    (ref as MutableRefObject<Konva.Group | null>).current = n;
  }, [ref]);

  const fireDrag = (handler?: (pos: { x: number; y: number }) => void) => (e: any) => {
    if (!handler) return;
    const pos = e.target.position();
    handler({ x: pos.x, y: pos.y });
  };

  const fireDragSnapped = (handler?: (pos: { x: number; y: number }) => void) => (e: any) => {
    const pos = e.target.position();
    const gx = Math.round(pos.x / snapToGrid) * snapToGrid;
    const gy = Math.round(pos.y / snapToGrid) * snapToGrid;
    try {
      e.target.position({ x: gx, y: gy });
    } catch {}
    if (handler) handler({ x: gx, y: gy });
  };

  const verticalLines = useMemo(() => {
    const lines: ReactNode[] = [];
    let xCursor = 0;
    for (let i = 0; i < columnCount - 1; i += 1) {
      xCursor += effectiveColWidths[i];
      const x = xCursor;
      const base = Math.max(1, strokeWidth);
      lines.push(
        <Line
          key={`v-${i}`}
          points={[x, 0, x, renderHeight]}
          stroke={gridColor}
          strokeWidth={hoveredSeparator?.type === 'col' && hoveredSeparator.index === i ? Math.max(2, base + 1) : base}
          dash={dashEnabled ? dash : undefined}
          perfectDrawEnabled={false}
        />,
      );
    }
    return lines;
  }, [columnCount, dash, dashEnabled, effectiveColWidths, gridColor, renderHeight, hoveredSeparator, strokeWidth]);

  const horizontalLines = useMemo(() => {
    const lines: ReactNode[] = [];
    let yCursor = 0;
    for (let i = 0; i < effectiveCombinedHeights.length - 1; i += 1) {
      yCursor += effectiveCombinedHeights[i];
      const y = yCursor;
      const base = Math.max(1, strokeWidth);
      lines.push(
        <Line
          key={`h-${i}`}
          points={[0, y, renderWidth, y]}
          stroke={gridColor}
          strokeWidth={hoveredSeparator?.type === 'row' && hoveredSeparator.index === i ? Math.max(2, base + 1) : base}
          dash={dashEnabled ? dash : undefined}
          perfectDrawEnabled={false}
        />,
      );
    }
    return lines;
  }, [dash, dashEnabled, effectiveCombinedHeights, gridColor, renderWidth, hoveredSeparator, strokeWidth]);

  const edgeHoverLines = useMemo(() => {
    const lines: ReactNode[] = [];
    const lastColIndex = Math.max(0, columnCount - 1);
    const lastRowIndex = Math.max(0, effectiveCombinedHeights.length - 1);
    if (hoveredSeparator?.type === 'col' && hoveredSeparator.index === lastColIndex) {
      lines.push(
        <Line
          key="hover-edge-right"
          points={[renderWidth, 0, renderWidth, renderHeight]}
          stroke={gridColor}
          strokeWidth={Math.max(2, Math.max(1, strokeWidth) + 1)}
          dash={dashEnabled ? dash : undefined}
          perfectDrawEnabled={false}
        />,
      );
    }
    if (hoveredSeparator?.type === 'row' && hoveredSeparator.index === lastRowIndex) {
      lines.push(
        <Line
          key="hover-edge-bottom"
          points={[0, renderHeight, renderWidth, renderHeight]}
          stroke={gridColor}
          strokeWidth={Math.max(2, Math.max(1, strokeWidth) + 1)}
          dash={dashEnabled ? dash : undefined}
          perfectDrawEnabled={false}
        />,
      );
    }
    return lines;
  }, [columnCount, dash, dashEnabled, effectiveCombinedHeights.length, gridColor, hoveredSeparator, renderHeight, renderWidth, strokeWidth]);

  const selectionOverlays = useMemo(() => {
    const selected = selection?.selectedKeys ?? [];
    if (selected.length === 0) return null;

    const overlays: ReactNode[] = [];
    const rendered = new Set<string>();

    for (const k of selected) {
      const { r, c } = parseCellKey(k);
      const mr = findMergedRegion(r, c);
      if (mr) {
        const key = `mr-${mr.startRow}-${mr.startCol}-${mr.endRow}-${mr.endCol}`;
        if (rendered.has(key)) continue;
        rendered.add(key);

        const x1 = colStarts[mr.startCol] ?? 0;
        const x2 = colStarts[mr.endCol + 1] ?? renderWidth;
        const y1 = rowStarts[mr.startRow] ?? 0;
        const y2 = rowStarts[mr.endRow + 1] ?? renderHeight;
      const w = Math.max(0, x2 - x1);
      const h = Math.max(0, y2 - y1);
        overlays.push(
        <Rect
            key={`sel-${key}`}
          x={x1}
          y={y1}
          width={w}
          height={h}
          fill="rgba(59,130,246,0.10)"
          stroke="#3B82F6"
          strokeWidth={2}
          listening={false}
          perfectDrawEnabled={false}
          />,
        );
        continue;
      }

      const key = `cell-${r}-${c}`;
      if (rendered.has(key)) continue;
      rendered.add(key);
    const x1 = colStarts[c] ?? 0;
    const x2 = colStarts[c + 1] ?? renderWidth;
    const y1 = rowStarts[r] ?? 0;
    const y2 = rowStarts[r + 1] ?? renderHeight;
    const w = Math.max(0, x2 - x1);
    const h = Math.max(0, y2 - y1);
      overlays.push(
      <Rect
          key={`sel-${key}`}
        x={x1}
        y={y1}
        width={w}
        height={h}
          fill="rgba(59,130,246,0.10)"
          stroke="#3B82F6"
          strokeWidth={2}
        listening={false}
        perfectDrawEnabled={false}
        />,
    );
    }

    return overlays;
  }, [colStarts, findMergedRegion, parseCellKey, renderHeight, renderWidth, rowStarts, selection?.selectedKeys]);

  const hasAnyCellText = useMemo(() => {
    return Object.keys(cellTextMap).length > 0;
  }, [cellTextMap]);

  const cellFillBackgrounds = useMemo(() => {
    const rows = effectiveCombinedHeights.length;
    const cols = columnCount;
    if (rows <= 0 || cols <= 0) return null;
    const rects: ReactNode[] = [];
    const radius = 6;

    for (let r = 0; r < rows; r += 1) {
      const y1 = rowStarts[r] ?? 0;
      for (let c = 0; c < cols; c += 1) {
        const mr = findMergedRegion(r, c);
        if (mr && !isMergedAnchor(mr, r, c)) continue;

        const startRow = mr ? mr.startRow : r;
        const endRow = mr ? mr.endRow : r;
        const startCol = mr ? mr.startCol : c;
        const endCol = mr ? mr.endCol : c;

        const k = cellKey(r, c);
        const style = (cellStyleMap[k] ?? {}) as TableCellStyle;
        const fill = style.fillColor;
        if (!fill) continue;

        const x1 = colStarts[startCol] ?? 0;
        const x2 = colStarts[endCol + 1] ?? renderWidth;
        const y2 = rowStarts[endRow + 1] ?? renderHeight;
        const w = Math.max(0, x2 - x1);
        const h = Math.max(0, y2 - y1);

        const isTop = startRow === 0;
        const isBottom = endRow === rows - 1;
        const isLeft = startCol === 0;
        const isRight = endCol === cols - 1;

        const cornerRadius =
          isTop || isBottom || isLeft || isRight
            ? [
                isTop && isLeft ? radius : 0,
                isTop && isRight ? radius : 0,
                isBottom && isRight ? radius : 0,
                isBottom && isLeft ? radius : 0,
              ]
            : 0;

        rects.push(
          <Rect
            key={`cell-fill-${k}`}
            x={x1}
            y={y1}
            width={w}
            height={h}
            fill={fill}
            cornerRadius={cornerRadius as any}
            listening={false}
            perfectDrawEnabled={false}
          />,
        );
      }
    }

    return rects;
  }, [cellStyleMap, colStarts, columnCount, effectiveCombinedHeights.length, findMergedRegion, renderHeight, renderWidth, rowStarts]);

  const cellTextGroups = useMemo(() => {
    const rows = effectiveCombinedHeights.length;
    const cols = columnCount;
    if (rows <= 0 || cols <= 0) return null;
    const fontSize = (attrs.fontSize as number) || 14;
    const fontFamily = (attrs.fontFamily as string) || 'Inter, system-ui, -apple-system, sans-serif';
    const groups: ReactNode[] = [];
    for (let r = 0; r < rows; r += 1) {
      const y1 = rowStarts[r] ?? 0;
      for (let c = 0; c < cols; c += 1) {
        const mr = findMergedRegion(r, c);
        if (mr && !isMergedAnchor(mr, r, c)) {
          continue;
        }
        const startCol = mr ? mr.startCol : c;
        const endCol = mr ? mr.endCol : c;
        const endRow = mr ? mr.endRow : r;

        const x1 = colStarts[startCol] ?? 0;
        const x2 = colStarts[endCol + 1] ?? renderWidth;
        const y2 = rowStarts[endRow + 1] ?? renderHeight;
        const w = Math.max(0, x2 - x1);
        const h = Math.max(0, y2 - y1);
        const k = cellKey(r, c);
        const cellTextId = `table-${nodeIdSafe}-cell-${r}-${c}`;
        const txt = cellTextMap[k] ?? '';
        const style = (cellStyleMap[k] ?? {}) as TableCellStyle;
        const textRect = getTableCellTextRect({ x: 0, y: 0, w, h }, style);
        const cellBorderColor = style.borderColor;
        const cellBorderWidth = typeof style.borderWidth === 'number' ? style.borderWidth : undefined;
        const cellBorderDash = Array.isArray(style.borderDash) && style.borderDash.length > 0 ? style.borderDash : undefined;
        const cellTextColor = style.textColor ?? textColor;
        const cellFontSize = typeof style.fontSize === 'number' ? style.fontSize : fontSize;
        const cellFontFamily = style.fontFamily ?? fontFamily;
        const headerRow = mr ? headerRowIndices.includes(mr.startRow) : headerRowIndices.includes(r);
        const headerCol = mr ? headerColIndices.includes(mr.startCol) : headerColIndices.includes(c);
        const cellFontWeight = style.fontWeight ?? (headerRow || headerCol ? 'bold' : 'normal');
        const cellFontStyle = style.fontStyle ?? 'normal';
        const cellTextDecoration = style.textDecoration;
        const cellAlign = style.textAlign ?? 'center';
        const cellVAlign = style.verticalAlign ?? 'middle';
        const rotation =
          typeof (style as any).textRotation === 'number' && Number.isFinite((style as any).textRotation)
            ? ((style as any).textRotation as 0 | 90 | -90 | 180)
            : 0;
        groups.push(
          <Group key={`cell-text-${k}`} x={x1} y={y1} listening={false}>
            <Rect
              name="text-bounds"
              x={0}
              y={0}
              width={w}
              height={h}
              fill="transparent"
              listening={false}
              perfectDrawEnabled={false}
            />
            {cellBorderColor && cellBorderWidth && cellBorderWidth > 0 ? (
              <Rect
                x={0}
                y={0}
                width={w}
                height={h}
                fill="transparent"
                stroke={cellBorderColor}
                strokeWidth={cellBorderWidth}
                dash={cellBorderDash}
                listening={false}
                perfectDrawEnabled={false}
              />
            ) : null}
            {rotation === 0 ? (
            <Text
              ref={getCellTextRefCb(k)}
              id={cellTextId}
              x={textRect.x}
              y={textRect.y}
              width={textRect.w}
              height={textRect.h}
              text={txt}
              fill={cellTextColor}
              fontSize={cellFontSize}
              fontFamily={cellFontFamily}
              fontStyle={(() => {
                const bold = cellFontWeight === 'bold';
                const italic = cellFontStyle === 'italic';
                if (bold && italic) return 'bold italic';
                if (bold) return 'bold';
                if (italic) return 'italic';
                return 'normal';
              })()}
              textDecoration={cellTextDecoration && cellTextDecoration !== 'none' ? cellTextDecoration : undefined}
              align={cellAlign}
              verticalAlign={cellVAlign}
              visible={editingCellKey !== k}
              listening={false}
              perfectDrawEnabled={false}
            />
            ) : (
              (() => {
                const isQuarterTurn = rotation === 90 || rotation === -90;
                const rotTextW = isQuarterTurn ? textRect.h : textRect.w;
                const rotTextH = isQuarterTurn ? textRect.w : textRect.h;
                const rotX = -rotTextW / 2;
                const rotY = -rotTextH / 2;
                return (
                  <Group x={textRect.x + textRect.w / 2} y={textRect.y + textRect.h / 2} rotation={rotation} listening={false}>
                    <Text
                      ref={getCellTextRefCb(k)}
                      id={cellTextId}
                      x={rotX}
                      y={rotY}
                      width={rotTextW}
                      height={rotTextH}
                      text={txt}
                      fill={cellTextColor}
                      fontSize={cellFontSize}
                      fontFamily={cellFontFamily}
                      fontStyle={(() => {
                        const bold = cellFontWeight === 'bold';
                        const italic = cellFontStyle === 'italic';
                        if (bold && italic) return 'bold italic';
                        if (bold) return 'bold';
                        if (italic) return 'italic';
                        return 'normal';
                      })()}
                      textDecoration={cellTextDecoration && cellTextDecoration !== 'none' ? cellTextDecoration : undefined}
                      align={cellAlign}
                      verticalAlign={cellVAlign}
                      visible={editingCellKey !== k}
                      listening={false}
                      perfectDrawEnabled={false}
                    />
                  </Group>
                );
              })()
            )}
          </Group>,
        );
      }
    }
    return groups;
  }, [attrs.fontFamily, attrs.fontSize, cellStyleMap, cellTextMap, colStarts, columnCount, editingCellKey, effectiveCombinedHeights.length, findMergedRegion, getCellTextRefCb, headerColIndices, headerRowIndices, renderHeight, renderWidth, rowStarts, textColor]);

  const headerCellBackgrounds = useMemo(() => {
    if (headerRowIndices.length === 0 && headerColIndices.length === 0) return null;
    const rows = effectiveCombinedHeights.length;
    const cols = columnCount;
    if (rows <= 0 || cols <= 0) return null;

    const keys = new Set<string>();
    const rects: ReactNode[] = [];
    const radius = 6;

    for (let r = 0; r < rows; r += 1) {
      if (!headerRowIndices.includes(r)) continue;
      const y1 = rowStarts[r] ?? 0;
      const y2 = rowStarts[r + 1] ?? renderHeight;
      const h = Math.max(0, y2 - y1);
      for (let c = 0; c < cols; c += 1) {
        const k = `${r},${c}`;
        if (keys.has(k)) continue;
        keys.add(k);
        const x1 = colStarts[c] ?? 0;
        const x2 = colStarts[c + 1] ?? renderWidth;
        const w = Math.max(0, x2 - x1);
        const isTop = r === 0;
        const isBottom = r === rows - 1;
        const isLeft = c === 0;
        const isRight = c === cols - 1;
        const cornerRadius =
          isTop || isBottom || isLeft || isRight
            ? [
                isTop && isLeft ? radius : 0,
                isTop && isRight ? radius : 0,
                isBottom && isRight ? radius : 0,
                isBottom && isLeft ? radius : 0,
              ]
            : 0;
        rects.push(
          <Rect
            key={`hdr-bg-${k}`}
            x={x1}
            y={y1}
            width={w}
            height={h}
            fill={headerFillColor}
            cornerRadius={cornerRadius as any}
            listening={false}
            perfectDrawEnabled={false}
          />,
        );
      }
    }

    for (let c = 0; c < cols; c += 1) {
      if (!headerColIndices.includes(c)) continue;
      const x1 = colStarts[c] ?? 0;
      const x2 = colStarts[c + 1] ?? renderWidth;
      const w = Math.max(0, x2 - x1);
      for (let r = 0; r < rows; r += 1) {
        const k = `${r},${c}`;
        if (keys.has(k)) continue;
        keys.add(k);
        const y1 = rowStarts[r] ?? 0;
        const y2 = rowStarts[r + 1] ?? renderHeight;
        const h = Math.max(0, y2 - y1);
        const isTop = r === 0;
        const isBottom = r === rows - 1;
        const isLeft = c === 0;
        const isRight = c === cols - 1;
        const cornerRadius =
          isTop || isBottom || isLeft || isRight
            ? [
                isTop && isLeft ? radius : 0,
                isTop && isRight ? radius : 0,
                isBottom && isRight ? radius : 0,
                isBottom && isLeft ? radius : 0,
              ]
            : 0;
        rects.push(
          <Rect
            key={`hdr-bg-${k}`}
            x={x1}
            y={y1}
            width={w}
            height={h}
            fill={headerFillColor}
            cornerRadius={cornerRadius as any}
            listening={false}
            perfectDrawEnabled={false}
          />,
        );
      }
    }

    return rects;
  }, [colStarts, columnCount, effectiveCombinedHeights.length, headerColIndices, headerFillColor, headerRowIndices, renderHeight, renderWidth, rowStarts]);

  const mergedRegionOverlays = useMemo(() => {
    if (mergedRegions.length === 0) return null;
    return mergedRegions.map((mr, idx) => {
      const startRow = typeof mr?.startRow === 'number' ? mr.startRow : NaN;
      const startCol = typeof mr?.startCol === 'number' ? mr.startCol : NaN;
      const endRow = typeof mr?.endRow === 'number' ? mr.endRow : NaN;
      const endCol = typeof mr?.endCol === 'number' ? mr.endCol : NaN;
      if (![startRow, startCol, endRow, endCol].every(Number.isFinite)) return null;
      if (endRow < startRow || endCol < startCol) return null;

      const x1 = colStarts[startCol] ?? 0;
      const x2 = colStarts[endCol + 1] ?? renderWidth;
      const y1 = rowStarts[startRow] ?? 0;
      const y2 = rowStarts[endRow + 1] ?? renderHeight;
      const w = Math.max(0, x2 - x1);
      const h = Math.max(0, y2 - y1);
      if (w <= 0 || h <= 0) return null;

      const fill = (showHeader && startRow === 0) || (showHeaderColumn && startCol === 0) ? headerFillColor : cellFillColor;
      const strokeW = Math.max(1, strokeWidth);

      // Render AFTER grid lines to hide internal grid, but BEFORE selection/text overlays.
      return (
        <Rect
          key={`merged-${idx}-${startRow}-${startCol}-${endRow}-${endCol}`}
          x={x1}
          y={y1}
          width={w}
          height={h}
          fill={fill}
          stroke={gridColor}
          strokeWidth={strokeW}
          dash={dashEnabled ? dash : undefined}
          listening={false}
          perfectDrawEnabled={false}
        />
      );
    });
  }, [
    cellFillColor,
    colStarts,
    dash,
    dashEnabled,
    gridColor,
    headerFillColor,
    mergedRegions,
    renderHeight,
    renderWidth,
    rowStarts,
    showHeader,
    showHeaderColumn,
    strokeWidth,
  ]);

  const scheduleDockedPreviewUpdate = useCallback((nextCombinedHeights?: number[], nextColWidths?: number[]) => {
    if (resizePreviewRafRef.current != null) return;
    resizePreviewRafRef.current = requestAnimationFrame(() => {
      resizePreviewRafRef.current = null;
      const ids = dockedResizeIdsRef.current;
      if (!ids || ids.length === 0) return;
      const store = useDiagramStore.getState();
      const liveTable = store.nodes.find((n) => n.id === node.id) ?? node;
      const attrsLive = ((liveTable as any).attrs ?? {}) as Record<string, unknown>;

      const mergedRegions = (attrsLive as any).mergedRegions;
      const effectiveWidths = nextColWidths ?? effectiveColWidths;
      const effectiveHeights = nextCombinedHeights ?? effectiveCombinedHeights;
      const body = showHeader ? effectiveHeights.slice(1) : effectiveHeights;
      const headerH = showHeader ? (effectiveHeights[0] ?? headerHeightFixed) : 0;
      const newW = effectiveWidths.reduce((s, n) => s + n, 0);
      const newH = effectiveHeights.reduce((s, n) => s + n, 0);

      const preview: any = {
        ...(liveTable as any),
        width: newW,
        w: newW,
        height: newH,
        h: newH,
        attrs: {
          ...attrsLive,
          rowHeights: body,
          columnWidths: effectiveWidths,
          headerHeight: headerH,
          mergedRegions,
        },
      };

      const dockedUpdates = computeDockedLayoutUpdatesForTable(preview, store.nodes)
        .map((u) => {
          const x = u.changes.x;
          const y = u.changes.y;
          const nextDock = (u.changes as any)?.attrs?.tableDock as TableDock | undefined;
          const existing = store.nodes.find((n) => n.id === u.nodeId) as any;
          const baseAttrs = ((existing as any)?.attrs ?? {}) as Record<string, unknown>;
          // During resize preview we intentionally don't dirty/persist store updates, but we DO need to
          // keep `tableDock.offset` in sync for fixed-layout nodes. Otherwise subsequent preview ticks
          // will recompute offsets from absolute x/y and the cell rect, causing drift/displacement.
          const nextAttrs = nextDock
            ? { ...baseAttrs, tableDock: { ...(baseAttrs as any).tableDock, ...nextDock } }
            : undefined;

          return { id: u.nodeId, updates: nextAttrs ? ({ x, y, attrs: nextAttrs } as any) : ({ x, y } as any) };
        })
        .filter((u) => typeof u.updates.x === 'number' && typeof u.updates.y === 'number' && ids.includes(u.id));

      if (dockedUpdates.length > 0) {
        store.updateNodes(dockedUpdates as any, true);
      }
    });
  }, [columnWidths, effectiveColWidths, effectiveCombinedHeights, headerHeightFixed, node, showHeader]);

  const applyAutoResizeAfterResize = useCallback((
    axis: 'row' | 'col',
    changedIndex: number,
    baseCombined: number[],
    baseWidths: number[],
  ): { combined: number[]; widths: number[] } => {
    const attrs0 = ((useDiagramStore.getState().nodes.find((n) => n.id === node.id) as any)?.attrs ?? (node as any).attrs ?? {}) as any;
    if (attrs0?.autoResizeCells !== true) return { combined: baseCombined, widths: baseWidths };

    const merged = Array.isArray(attrs0?.mergedRegions) ? (attrs0.mergedRegions as MergedRegion[]) : [];
    const showHeader = attrs0?.showHeader !== false;

    const store = useDiagramStore.getState();
    const docked = store.nodes.filter((n) => (n as any)?.attrs?.tableDock?.tableId === node.id) as any[];
    if (docked.length === 0) return { combined: baseCombined, widths: baseWidths };

    const combined = [...baseCombined];
    const widths = [...baseWidths];
    const rowCount = clampCount(attrs0?.rowCount, 3);
    const colCount = clampCount(attrs0?.columnCount, 3);
    const rows = showHeader ? rowCount + 1 : rowCount;

    const headerH = showHeader ? (combined[0] ?? headerHeightFixed) : 0;
    const bodyHeights = showHeader ? combined.slice(1) : combined;
    const totalW0 = widths.reduce((s, n) => s + n, 0);
    const totalH0 = combined.reduce((s, n) => s + n, 0);
    const previewTableBase: any = {
      ...(store.nodes.find((n) => n.id === node.id) ?? node),
      width: totalW0,
      w: totalW0,
      height: totalH0,
      h: totalH0,
      attrs: {
        ...attrs0,
        rowHeights: bodyHeights,
        columnWidths: widths,
        headerHeight: headerH,
      },
    };

    const affectedCellKeys = new Set<`${number},${number}`>();
    for (const dn of docked) {
      const dock = (dn as any)?.attrs?.tableDock as TableDock | undefined;
      if (!dock) continue;
      const key = dock.cellKey;
      const [rStr, cStr] = String(key).split(',');
      const r = Number(rStr);
      const c = Number(cStr);
      if (!Number.isFinite(r) || !Number.isFinite(c)) continue;
      const mr = merged.find((m) => containsCell(m, r, c)) ?? null;
      const affects =
        axis === 'row'
          ? mr
            ? changedIndex >= mr.startRow && changedIndex <= mr.endRow
            : r === changedIndex
          : mr
            ? changedIndex >= mr.startCol && changedIndex <= mr.endCol
            : c === changedIndex;
      if (affects) {
        affectedCellKeys.add(`${mr ? mr.startRow : r},${mr ? mr.startCol : c}`);
      }
    }
    if (affectedCellKeys.size === 0) return { combined: baseCombined, widths: baseWidths };

    const colGrowByIdx = new Map<number, number>();
    const rowGrowByIdx = new Map<number, number>(); // render-space row index (includes header row)

    for (const cellKey of affectedCellKeys) {
      const hit = getTableCellRectByKey(previewTableBase, cellKey);
      if (!hit) continue;

      const anchor = (() => {
        const [rStr, cStr] = String(hit.cellKey).split(',');
        return { r: Number(rStr), c: Number(cStr) };
      })();
      if (!Number.isFinite(anchor.r) || !Number.isFinite(anchor.c)) continue;

      const mr = merged.find((m) => containsCell(m, anchor.r, anchor.c)) ?? null;
      const targetRow = mr ? mr.endRow : anchor.r;
      const targetCol = mr ? mr.endCol : anchor.c;

      const cellDocked = docked.filter((n) => {
        const d = (n as any)?.attrs?.tableDock as TableDock | undefined;
        if (!d) return false;
        if (d.tableId !== node.id) return false;
        if (d.cellKey === hit.cellKey) return true;
        if (!mr) return false;
        const [rrStr, ccStr] = String(d.cellKey).split(',');
        const rr = Number(rrStr);
        const cc = Number(ccStr);
        return Number.isFinite(rr) && Number.isFinite(cc) && containsCell(mr, rr, cc);
      });
      if (cellDocked.length === 0) continue;

      const config = resolveCellLayoutConfig(previewTableBase.attrs, hit.cellKey as any);
      const layoutRect = hit.contentRect;
      const gap = typeof (config.params as any)?.gap === 'number' ? Math.max(0, (config.params as any).gap) : 8;
      const padding = typeof (config.params as any)?.padding === 'number' ? Math.max(0, (config.params as any).padding) : 8;

      const items = cellDocked
        .slice()
        .sort((a: any, b: any) => {
          const za = Number((a as any).zIndex ?? 0);
          const zb = Number((b as any).zIndex ?? 0);
          if (za !== zb) return za - zb;
          return String(a.id).localeCompare(String(b.id));
        })
        .map((n: any) => {
          const w = (n.w ?? n.width ?? 60) as number;
          const h = (n.h ?? n.height ?? 60) as number;
          const dock = (n as any)?.attrs?.tableDock as TableDock | undefined;
          const offX =
            typeof dock?.offset?.x === 'number' && Number.isFinite(dock.offset.x) ? dock.offset.x : (Number(n.x) || 0) - layoutRect.x;
          const offY =
            typeof dock?.offset?.y === 'number' && Number.isFinite(dock.offset.y) ? dock.offset.y : (Number(n.y) || 0) - layoutRect.y;
          return { w: Number(w) || 60, h: Number(h) || 60, offX, offY };
        });

      let requiredW = layoutRect.w;
      let requiredH = layoutRect.h;

      if (config.algorithmId === 'fixed') {
        let maxX = 0;
        let maxY = 0;
        for (const it of items) {
          maxX = Math.max(maxX, Math.max(0, it.offX) + it.w);
          maxY = Math.max(maxY, Math.max(0, it.offY) + it.h);
        }
        requiredW = Math.max(requiredW, maxX);
        requiredH = Math.max(requiredH, maxY);
      } else {
        const maxW = Math.max(...items.map((i) => i.w), 60);
        const maxH = Math.max(...items.map((i) => i.h), 60);
        const availableW = Math.max(1, layoutRect.w - padding * 2);
        const colsCurrent = Math.max(1, Math.floor((availableW + gap) / (maxW + gap)));
        const rowsCurrent = Math.max(1, Math.ceil(items.length / colsCurrent));
        const needW = padding * 2 + colsCurrent * maxW + (colsCurrent - 1) * gap;
        const needH = padding * 2 + rowsCurrent * maxH + (rowsCurrent - 1) * gap;
        requiredW = Math.max(requiredW, needW);
        requiredH = Math.max(requiredH, needH);

        // If the user resized height (row drag) and content would overflow vertically,
        // prefer growing width to pack into fewer rows (grow-only).
        if (axis === 'row' && requiredH > layoutRect.h) {
          const availableH = Math.max(1, layoutRect.h - padding * 2);
          const maxRowsFit = Math.max(0, Math.floor((availableH + gap) / (maxH + gap)));
          if (maxRowsFit >= 1) {
            const colsWanted = Math.min(items.length, Math.max(1, Math.ceil(items.length / maxRowsFit)));
            const rowsWanted = Math.max(1, Math.ceil(items.length / colsWanted));
            if (rowsWanted <= maxRowsFit) {
              const needW2 = padding * 2 + colsWanted * maxW + (colsWanted - 1) * gap;
              requiredW = Math.max(requiredW, needW2);
              requiredH = layoutRect.h;
            }
          }
        }
      }

      const deltaW = Math.max(0, requiredW - layoutRect.w);
      const deltaH = Math.max(0, requiredH - layoutRect.h);

      if (deltaW > 0) colGrowByIdx.set(targetCol, Math.max(colGrowByIdx.get(targetCol) ?? 0, deltaW));
      if (deltaH > 0) rowGrowByIdx.set(targetRow, Math.max(rowGrowByIdx.get(targetRow) ?? 0, deltaH));
    }

    if (colGrowByIdx.size === 0 && rowGrowByIdx.size === 0) return { combined: baseCombined, widths: baseWidths };

    for (const [c, dw] of colGrowByIdx.entries()) {
      const idx = Math.max(0, Math.min(colCount - 1, c));
      widths[idx] = Math.max(MIN_CELL, (widths[idx] ?? MIN_CELL) + dw);
    }

    for (const [r, dh] of rowGrowByIdx.entries()) {
      const rr = Math.max(0, Math.min(rows - 1, r));
      if (showHeader && rr === 0) {
        combined[0] = Math.max(MIN_CELL, (combined[0] ?? headerHeightFixed) + dh);
      } else {
        combined[rr] = Math.max(MIN_CELL, (combined[rr] ?? MIN_CELL) + dh);
      }
    }

    return { combined, widths };
  }, [headerHeightFixed, node, showHeader]);

  const getGroupAbsPos = () => {
    try {
      const p = groupRef.current?.getAbsolutePosition();
      if (p && Number.isFinite(p.x) && Number.isFinite(p.y)) return p;
    } catch {}
    return { x: ((node as any).x ?? node.x ?? 0) as number, y: ((node as any).y ?? node.y ?? 0) as number };
  };

  const buildRowHandles = () => {
    const handles: ReactNode[] = [];
    const segments = effectiveCombinedHeights.length;
    if (segments < 2) return handles;
    let yCursor = 0;
    for (let i = 0; i < segments - 1; i += 1) {
      yCursor += effectiveCombinedHeights[i];
      const y = yCursor;
      handles.push(
        <Line
          key={`row-handle-${i}`}
          x={0}
          y={y}
          points={[0, 0, renderWidth, 0]}
          stroke="transparent"
          strokeWidth={1}
          hitStrokeWidth={12}
          draggable={canWrite}
          dragBoundFunc={(pos) => {
            const g = getGroupAbsPos();
            return { x: g.x, y: pos.y };
          }}
          onMouseEnter={() => {
            setHoveredSeparator({ type: 'row', index: i });
            setStageCursor(rowResizeCursor);
          }}
          onMouseLeave={() => {
            setHoveredSeparator(null);
            if (!isResizing) setStageCursor('default');
          }}
          onDragStart={(e) => {
            e.cancelBubble = true;
            setIsResizing(true);
            setStageCursor(rowResizeCursor);
              try {
                const store = useDiagramStore.getState();
                const docked = store.nodes.filter((n) => (n as any)?.attrs?.tableDock?.tableId === node.id).map((n) => n.id);
                dockedResizeIdsRef.current = docked;
                docked.forEach((id) => store.setDragging(id, true));
              } catch {}
              rowDragRef.current = { index: i, startY: e.target.y(), baseHeights: [...effectiveCombinedHeights] };
          }}
          onDragMove={(e) => {
            e.cancelBubble = true;
            const ctx = rowDragRef.current;
            const baseStart = ctx?.startY ?? y;
            const delta = e.target.y() - baseStart;
            const base = ctx?.baseHeights ?? effectiveCombinedHeights;
            const next = [...base];
            next[i] = Math.max(MIN_CELL, base[i] + delta);
            const baseWidths = draftColWidths ?? effectiveColWidths;
            const auto = applyAutoResizeAfterResize('row', i, next, baseWidths);
            setDraftCombinedHeights(auto.combined);
            setDraftColWidths(auto.widths);
            scheduleDockedPreviewUpdate(auto.combined, auto.widths);
          }}
          onDragEnd={(e) => {
            e.cancelBubble = true;
            setIsResizing(false);
            const ctx = rowDragRef.current;
            const baseStart = ctx?.startY ?? y;
            e.target.position({ x: 0, y: baseStart });
            const finalCombined = (() => {
              const base = ctx?.baseHeights ?? effectiveCombinedHeights;
              const draft = draftCombinedHeights;
              if (!draft) return base;
              return draft;
            })();
            const auto = applyAutoResizeAfterResize('row', i, finalCombined, effectiveColWidths);
            const nextCombined = auto.combined;
            const nextWidths = auto.widths;
            const body = showHeader ? nextCombined.slice(1) : nextCombined;
            const newTotalHeight = nextCombined.reduce((s, n) => s + n, 0);
            const newTotalWidth = nextWidths.reduce((s, n) => s + n, 0);
            getCommandManager().execute(new ResizeTableGridCommand({
              tableId: node.id,
              rowHeights: body,
              columnWidths: nextWidths,
              headerHeight: showHeader ? nextCombined[0] : 0,
              width: newTotalWidth,
              height: newTotalHeight,
            }));
            rowDragRef.current = null;
            setDraftCombinedHeights(null);
            setDraftColWidths(null);
            setHoveredSeparator(null);
            setStageCursor('default');
            try {
              const store = useDiagramStore.getState();
              dockedResizeIdsRef.current.forEach((id) => store.setDragging(id, false));
              dockedResizeIdsRef.current = [];
            } catch {}
          }}
          listening={canWrite}
        />,
      );
    }
    handles.push(
      <Line
        key="row-handle-bottom-edge"
        x={0}
        y={renderHeight}
        points={[0, 0, renderWidth, 0]}
        stroke="transparent"
        strokeWidth={1}
        hitStrokeWidth={12}
        draggable={canWrite}
        dragBoundFunc={(pos) => {
          const g = getGroupAbsPos();
          return { x: g.x, y: pos.y };
        }}
        onMouseEnter={() => {
          setHoveredSeparator({ type: 'row', index: segments - 1 });
          setStageCursor(rowResizeCursor);
        }}
        onMouseLeave={() => {
          setHoveredSeparator(null);
          if (!isResizing) setStageCursor('default');
        }}
        onDragStart={(e) => {
          e.cancelBubble = true;
          setIsResizing(true);
          setStageCursor(rowResizeCursor);
          try {
            const store = useDiagramStore.getState();
            const docked = store.nodes.filter((n) => (n as any)?.attrs?.tableDock?.tableId === node.id).map((n) => n.id);
            dockedResizeIdsRef.current = docked;
            docked.forEach((id) => store.setDragging(id, true));
          } catch {}
          rowDragRef.current = { index: segments - 1, startY: e.target.y(), baseHeights: [...effectiveCombinedHeights] };
        }}
        onDragMove={(e) => {
          e.cancelBubble = true;
          const ctx = rowDragRef.current;
          const baseStart = ctx?.startY ?? renderHeight;
          const delta = e.target.y() - baseStart;
          const base = ctx?.baseHeights ?? effectiveCombinedHeights;
          const next = [...base];
          const lastIdx = next.length - 1;
          next[lastIdx] = Math.max(MIN_CELL, base[lastIdx] + delta);
          const baseWidths = draftColWidths ?? effectiveColWidths;
          const auto = applyAutoResizeAfterResize('row', segments - 1, next, baseWidths);
          setDraftCombinedHeights(auto.combined);
          setDraftColWidths(auto.widths);
          scheduleDockedPreviewUpdate(auto.combined, auto.widths);
        }}
        onDragEnd={(e) => {
          e.cancelBubble = true;
          setIsResizing(false);
          const ctx = rowDragRef.current;
          const baseStart = ctx?.startY ?? renderHeight;
          e.target.position({ x: 0, y: baseStart });
          const finalCombined = draftCombinedHeights ?? (ctx?.baseHeights ?? effectiveCombinedHeights);
          const auto = applyAutoResizeAfterResize('row', segments - 1, finalCombined, effectiveColWidths);
          const nextCombined = auto.combined;
          const nextWidths = auto.widths;
          const body = showHeader ? nextCombined.slice(1) : nextCombined;
          const newTotalHeight = nextCombined.reduce((s, n) => s + n, 0);
          const newTotalWidth = nextWidths.reduce((s, n) => s + n, 0);
          getCommandManager().execute(new ResizeTableGridCommand({
            tableId: node.id,
            rowHeights: body,
            columnWidths: nextWidths,
            headerHeight: showHeader ? nextCombined[0] : 0,
            width: newTotalWidth,
            height: newTotalHeight,
          }));
          rowDragRef.current = null;
          setDraftCombinedHeights(null);
          setDraftColWidths(null);
          setHoveredSeparator(null);
          setStageCursor('default');
          try {
            const store = useDiagramStore.getState();
            dockedResizeIdsRef.current.forEach((id) => store.setDragging(id, false));
            dockedResizeIdsRef.current = [];
          } catch {}
        }}
        listening={canWrite}
      />,
    );
    return handles;
  };

  const buildColHandles = () => {
    const handles: ReactNode[] = [];
    if (columnCount < 2) return handles;
    let xCursor = 0;
    for (let i = 0; i < columnCount - 1; i += 1) {
      xCursor += effectiveColWidths[i];
      const x = xCursor;
      handles.push(
        <Line
          key={`col-handle-${i}`}
          x={x}
          y={0}
          points={[0, 0, 0, renderHeight]}
          stroke="transparent"
          strokeWidth={1}
          hitStrokeWidth={12}
          draggable={canWrite}
          dragBoundFunc={(pos) => {
            const g = getGroupAbsPos();
            return { x: pos.x, y: g.y };
          }}
          onMouseEnter={() => {
            setHoveredSeparator({ type: 'col', index: i });
            setStageCursor(colResizeCursor);
          }}
          onMouseLeave={() => {
            setHoveredSeparator(null);
            if (!isResizing) setStageCursor('default');
          }}
          onDragStart={(e) => {
            e.cancelBubble = true;
            setIsResizing(true);
            setStageCursor(colResizeCursor);
            try {
              const store = useDiagramStore.getState();
              const docked = store.nodes.filter((n) => (n as any)?.attrs?.tableDock?.tableId === node.id).map((n) => n.id);
              dockedResizeIdsRef.current = docked;
              docked.forEach((id) => store.setDragging(id, true));
            } catch {}
            colDragRef.current = { index: i, startX: e.target.x(), baseWidths: [...effectiveColWidths] };
          }}
          onDragMove={(e) => {
            e.cancelBubble = true;
            const ctx = colDragRef.current;
            const baseStart = ctx?.startX ?? x;
            const delta = e.target.x() - baseStart;
            const base = ctx?.baseWidths ?? effectiveColWidths;
            const next = [...base];
            next[i] = Math.max(MIN_CELL, base[i] + delta);
            const baseCombined = draftCombinedHeights ?? effectiveCombinedHeights;
            const auto = applyAutoResizeAfterResize('col', i, baseCombined, next);
            setDraftCombinedHeights(auto.combined);
            setDraftColWidths(auto.widths);
            scheduleDockedPreviewUpdate(auto.combined, auto.widths);
          }}
          onDragEnd={(e) => {
            e.cancelBubble = true;
            setIsResizing(false);
            const ctx = colDragRef.current;
            const baseStart = ctx?.startX ?? x;
            e.target.position({ x: baseStart, y: 0 });
            const finalWidths = draftColWidths ?? effectiveColWidths;
            const auto = applyAutoResizeAfterResize('col', i, effectiveCombinedHeights, finalWidths);
            const nextCombined = auto.combined;
            const nextWidths = auto.widths;
            const newWidth = nextWidths.reduce((s, n) => s + n, 0);
            const newHeight = nextCombined.reduce((s, n) => s + n, 0);
            const bodyHeights = showHeader ? nextCombined.slice(1) : nextCombined;
            getCommandManager().execute(new ResizeTableGridCommand({
              tableId: node.id,
              rowHeights: bodyHeights,
              columnWidths: nextWidths,
              headerHeight: showHeader ? nextCombined[0] : 0,
              width: newWidth,
              height: newHeight,
            }));
            colDragRef.current = null;
            setDraftColWidths(null);
            setHoveredSeparator(null);
            setStageCursor('default');
            try {
              const store = useDiagramStore.getState();
              dockedResizeIdsRef.current.forEach((id) => store.setDragging(id, false));
              dockedResizeIdsRef.current = [];
            } catch {}
          }}
          listening={canWrite}
        />,
      );
    }
    handles.push(
      <Line
        key="col-handle-right-edge"
        x={renderWidth}
        y={0}
        points={[0, 0, 0, renderHeight]}
        stroke="transparent"
        strokeWidth={1}
        hitStrokeWidth={12}
        draggable={canWrite}
        dragBoundFunc={(pos) => {
          const g = getGroupAbsPos();
          return { x: pos.x, y: g.y };
        }}
        onMouseEnter={() => {
          setHoveredSeparator({ type: 'col', index: columnCount - 1 });
          setStageCursor(colResizeCursor);
        }}
        onMouseLeave={() => {
          setHoveredSeparator(null);
          if (!isResizing) setStageCursor('default');
        }}
        onDragStart={(e) => {
          e.cancelBubble = true;
          setIsResizing(true);
          setStageCursor(colResizeCursor);
          try {
            const store = useDiagramStore.getState();
            const docked = store.nodes.filter((n) => (n as any)?.attrs?.tableDock?.tableId === node.id).map((n) => n.id);
            dockedResizeIdsRef.current = docked;
            docked.forEach((id) => store.setDragging(id, true));
          } catch {}
          colDragRef.current = { index: columnCount - 1, startX: e.target.x(), baseWidths: [...effectiveColWidths] };
        }}
        onDragMove={(e) => {
          e.cancelBubble = true;
          const ctx = colDragRef.current;
          const baseStart = ctx?.startX ?? renderWidth;
          const delta = e.target.x() - baseStart;
          const base = ctx?.baseWidths ?? effectiveColWidths;
          const next = [...base];
          const lastIdx = next.length - 1;
          next[lastIdx] = Math.max(MIN_CELL, base[lastIdx] + delta);
          const baseCombined = draftCombinedHeights ?? effectiveCombinedHeights;
          const auto = applyAutoResizeAfterResize('col', columnCount - 1, baseCombined, next);
          setDraftCombinedHeights(auto.combined);
          setDraftColWidths(auto.widths);
          scheduleDockedPreviewUpdate(auto.combined, auto.widths);
        }}
        onDragEnd={(e) => {
          e.cancelBubble = true;
          setIsResizing(false);
          const ctx = colDragRef.current;
          const baseStart = ctx?.startX ?? renderWidth;
          e.target.position({ x: baseStart, y: 0 });
          const finalWidths = draftColWidths ?? (ctx?.baseWidths ?? effectiveColWidths);
          const auto = applyAutoResizeAfterResize('col', columnCount - 1, effectiveCombinedHeights, finalWidths);
          const nextCombined = auto.combined;
          const nextWidths = auto.widths;
          const newWidth = nextWidths.reduce((s, n) => s + n, 0);
          const newHeight = nextCombined.reduce((s, n) => s + n, 0);
          const bodyHeights = showHeader ? nextCombined.slice(1) : nextCombined;
          getCommandManager().execute(new ResizeTableGridCommand({
            tableId: node.id,
            rowHeights: bodyHeights,
            columnWidths: nextWidths,
            headerHeight: showHeader ? nextCombined[0] : 0,
            width: newWidth,
            height: newHeight,
          }));
          colDragRef.current = null;
          setDraftColWidths(null);
          setHoveredSeparator(null);
          setStageCursor('default');
          try {
            const store = useDiagramStore.getState();
            dockedResizeIdsRef.current.forEach((id) => store.setDragging(id, false));
            dockedResizeIdsRef.current = [];
          } catch {}
        }}
        listening={canWrite}
      />,
    );
    return handles;
  };

  const buildRowReorderHandles = () => {
    const handles: ReactNode[] = [];
    const segments = effectiveCombinedHeights.length;
    if (!canShowReorderHandles) return handles;
    const size = 10;
    const r = Math.max(0, Math.min(segments - 1, activeCellParsedForReorder?.r ?? 0));
      const y1 = rowStarts[r] ?? 0;
      const y2 = rowStarts[r + 1] ?? renderHeight;
      const y = (y1 + y2) / 2 - size / 2;
      handles.push(
        <Rect
          key={`row-reorder-${r}`}
          x={2}
          y={y}
          width={size}
          height={size}
          cornerRadius={2}
          fill="#64748B"
          opacity={reorderDrag?.axis === 'row' && reorderDrag.fromIndex === r ? 0.9 : canReorder ? 0.35 : 0.22}
          draggable={canReorder}
          dragBoundFunc={(pos) => {
            const g = getGroupAbsPos();
            const yy = Math.max(g.y, Math.min(pos.y, g.y + renderHeight - size));
            return { x: g.x + 2, y: yy };
          }}
          onMouseDown={(e) => {
            e.cancelBubble = true;
            try {
              groupRef.current?.stopDrag();
              e.target?.startDrag();
            } catch {}
          }}
          onMouseEnter={() => setStageCursor('grab')}
          onMouseLeave={() => {
            if (!reorderDrag) setStageCursor('default');
          }}
          onDragStart={(e) => {
            e.cancelBubble = true;
            try {
              groupRef.current?.stopDrag();
            } catch {}
            setStageCursor('grabbing');
            setIsReordering(true);
            const p = rowStarts[r] ?? 0;
            const y1 = rowStarts[r] ?? 0;
            const y2 = rowStarts[r + 1] ?? renderHeight;
            setReorderDrag({ axis: 'row', fromIndex: r, insertionIndex: r, pos: p, dragPos: (y1 + y2) / 2 });
          }}
          onDragMove={(e) => {
            e.cancelBubble = true;
            const pointer = groupRef.current?.getRelativePointerPosition?.();
            const localY =
              pointer && Number.isFinite(pointer.y)
                ? pointer.y
                : (() => {
            const g = getGroupAbsPos();
            const abs = e.target.getAbsolutePosition?.();
                    if (!abs || !Number.isFinite(abs.y)) return null;
                    return (abs.y - g.y) + size / 2;
                  })();
            if (localY === null) return;
            const insertionIndex = getRowInsertionIndex(localY);
            const p = rowStarts[insertionIndex] ?? renderHeight;
            setReorderDrag((prev) => (prev && prev.axis === 'row' ? { ...prev, insertionIndex, pos: p, dragPos: localY } : prev));
          }}
          onDragEnd={(e) => {
            e.cancelBubble = true;
            setReorderDrag(null);
            setIsReordering(false);
            setStageCursor('default');

            const pointer = groupRef.current?.getRelativePointerPosition?.();
            const localY =
              pointer && Number.isFinite(pointer.y)
                ? pointer.y
                : (() => {
            const g = getGroupAbsPos();
            const abs = e.target.getAbsolutePosition?.();
                    if (!abs || !Number.isFinite(abs.y)) return null;
                    return (abs.y - g.y) + size / 2;
                  })();
            if (localY === null) return;
            const insertionIndex = getRowInsertionIndex(localY);
            const segmentsCount = effectiveCombinedHeights.length;
            const insertion = Math.max(0, Math.min(insertionIndex, segmentsCount));
            const from = r;
            const to = insertion > from ? insertion - 1 : insertion;
            if (to === from) return;
            if (!isTableAxisReorderMergeSafe(mergedRegions, 'row', segmentsCount, from, to)) {
              toast.warning('That move would split a merged cell. Unmerge it first, then reorder.');
              return;
            }
            getCommandManager().execute(new ReorderTableAxisCommand(node.id, 'row', from, to));

            // Handles are UI-only; always snap back to the row/col edge after drop
            e.target.position({ x: 2, y });
          }}
          listening={canShowReorderHandles}
        />,
      );
    return handles;
  };

  const buildColReorderHandles = () => {
    const handles: ReactNode[] = [];
    if (!canShowReorderHandles) return handles;
    const size = 10;
    const c = Math.max(0, Math.min(columnCount - 1, activeCellParsedForReorder?.c ?? 0));
      const x1 = colStarts[c] ?? 0;
      const x2 = colStarts[c + 1] ?? renderWidth;
      const x = (x1 + x2) / 2 - size / 2;
      handles.push(
        <Rect
          key={`col-reorder-${c}`}
          x={x}
          y={2}
          width={size}
          height={size}
          cornerRadius={2}
          fill="#64748B"
          opacity={reorderDrag?.axis === 'col' && reorderDrag.fromIndex === c ? 0.9 : canReorder ? 0.35 : 0.22}
          draggable={canReorder}
          dragBoundFunc={(pos) => {
            const g = getGroupAbsPos();
            const xx = Math.max(g.x, Math.min(pos.x, g.x + renderWidth - size));
            return { x: xx, y: g.y + 2 };
          }}
          onMouseDown={(e) => {
            e.cancelBubble = true;
            try {
              groupRef.current?.stopDrag();
              e.target?.startDrag();
            } catch {}
          }}
          onMouseEnter={() => setStageCursor('grab')}
          onMouseLeave={() => {
            if (!reorderDrag) setStageCursor('default');
          }}
          onDragStart={(e) => {
            e.cancelBubble = true;
            try {
              groupRef.current?.stopDrag();
            } catch {}
            setStageCursor('grabbing');
            setIsReordering(true);
            const p = colStarts[c] ?? 0;
            const x1 = colStarts[c] ?? 0;
            const x2 = colStarts[c + 1] ?? renderWidth;
            setReorderDrag({ axis: 'col', fromIndex: c, insertionIndex: c, pos: p, dragPos: (x1 + x2) / 2 });
          }}
          onDragMove={(e) => {
            e.cancelBubble = true;
            const pointer = groupRef.current?.getRelativePointerPosition?.();
            const localX =
              pointer && Number.isFinite(pointer.x)
                ? pointer.x
                : (() => {
            const g = getGroupAbsPos();
            const abs = e.target.getAbsolutePosition?.();
                    if (!abs || !Number.isFinite(abs.x)) return null;
                    return (abs.x - g.x) + size / 2;
                  })();
            if (localX === null) return;
            const insertionIndex = getColInsertionIndex(localX);
            const p = colStarts[insertionIndex] ?? renderWidth;
            setReorderDrag((prev) => (prev && prev.axis === 'col' ? { ...prev, insertionIndex, pos: p, dragPos: localX } : prev));
          }}
          onDragEnd={(e) => {
            e.cancelBubble = true;
            setReorderDrag(null);
            setIsReordering(false);
            setStageCursor('default');

            const pointer = groupRef.current?.getRelativePointerPosition?.();
            const localX =
              pointer && Number.isFinite(pointer.x)
                ? pointer.x
                : (() => {
            const g = getGroupAbsPos();
            const abs = e.target.getAbsolutePosition?.();
                    if (!abs || !Number.isFinite(abs.x)) return null;
                    return (abs.x - g.x) + size / 2;
                  })();
            if (localX === null) return;
            const insertionIndex = getColInsertionIndex(localX);
            const segmentsCount = columnCount;
            const insertion = Math.max(0, Math.min(insertionIndex, segmentsCount));
            const from = c;
            const to = insertion > from ? insertion - 1 : insertion;
            if (to === from) return;
            if (!isTableAxisReorderMergeSafe(mergedRegions, 'col', segmentsCount, from, to)) {
              toast.warning('That move would split a merged cell. Unmerge it first, then reorder.');
              return;
            }
            getCommandManager().execute(new ReorderTableAxisCommand(node.id, 'col', from, to));

            // Handles are UI-only; always snap back to the row/col edge after drop
            e.target.position({ x, y: 2 });
          }}
          listening={canShowReorderHandles}
      />,
    );
    return handles;
  };

  return (
    <Group
      id={node.id}
      name="shape"
      ref={setCombinedRef}
      x={(node as any).x || 0}
      y={(node as any).y || 0}
      width={renderWidth}
      height={renderHeight}
      draggable={canWrite && !isResizing}
      onClick={handleGroupClick}
      onDblClick={handleGroupDoubleClick}
      onMouseMove={handleGroupMouseMove}
      onMouseLeave={handleGroupMouseLeave}
      onDragStart={fireDrag(onDragStart)}
      onDragMove={fireDrag(onDragMove)}
      onDragEnd={fireDragSnapped(onDragEnd)}
      onTransformStart={onTransformStart}
      onTransformEnd={onTransformEnd}
      onTransform={onTransform}
      listening
    >
      <Rect
        x={0}
        y={0}
        width={renderWidth}
        height={renderHeight}
        fill={cellFillColor}
        stroke={gridColor}
        strokeWidth={Math.max(1, strokeWidth)}
        dash={dashEnabled ? dash : undefined}
        cornerRadius={6}
      />
      {showHeader && (
        <Rect
          x={0}
          y={0}
          width={renderWidth}
          height={renderHeaderHeight}
          fill={headerFillColor}
          cornerRadius={[6, 6, 0, 0]}
          strokeEnabled={false}
        />
      )}
      {headerCellBackgrounds}
      {cellFillBackgrounds}
      {verticalLines}
      {horizontalLines}
      {mergedRegionOverlays}
      <Rect
        x={0}
        y={0}
        width={renderWidth}
        height={renderHeight}
        fill="transparent"
        stroke={gridColor}
        strokeWidth={Math.max(1, strokeWidth)}
        dash={dashEnabled ? dash : undefined}
        cornerRadius={6}
        listening={false}
        perfectDrawEnabled={false}
      />
      {reorderDrag?.axis === 'row' && (() => {
        const from = reorderDrag.fromIndex;
        const y1 = rowStarts[from] ?? 0;
        const y2 = rowStarts[from + 1] ?? renderHeight;
        const h = Math.max(0, y2 - y1);
        const center = reorderDrag.dragPos;
        const y = Math.max(0, Math.min(center - h / 2, Math.max(0, renderHeight - h)));
        return (
          <>
            <Rect x={0} y={y1} width={renderWidth} height={h} fill="rgba(15,23,42,0.04)" listening={false} perfectDrawEnabled={false} />
            <Rect x={0} y={y} width={renderWidth} height={h} fill="rgba(59,130,246,0.10)" stroke="#3B82F6" strokeWidth={1.5} listening={false} perfectDrawEnabled={false} />
          </>
        );
      })()}
      {reorderDrag?.axis === 'col' && (() => {
        const from = reorderDrag.fromIndex;
        const x1 = colStarts[from] ?? 0;
        const x2 = colStarts[from + 1] ?? renderWidth;
        const w = Math.max(0, x2 - x1);
        const center = reorderDrag.dragPos;
        const x = Math.max(0, Math.min(center - w / 2, Math.max(0, renderWidth - w)));
        return (
          <>
            <Rect x={x1} y={0} width={w} height={renderHeight} fill="rgba(15,23,42,0.04)" listening={false} perfectDrawEnabled={false} />
            <Rect x={x} y={0} width={w} height={renderHeight} fill="rgba(59,130,246,0.10)" stroke="#3B82F6" strokeWidth={1.5} listening={false} perfectDrawEnabled={false} />
          </>
        );
      })()}
      {edgeHoverLines}
      {selectionOverlays}
      {cellTextGroups}
      {label && !hasAnyCellText && (
        <Text
          x={12}
          y={showHeader ? renderHeaderHeight + 8 : 8}
          width={renderWidth - 24}
          height={renderHeight - (showHeader ? renderHeaderHeight + 16 : 16)}
          text={label}
          fill={textColor}
          align="center"
          verticalAlign="middle"
          fontSize={14}
          ellipsis
        />
      )}
      {reorderDrag?.axis === 'row' && (
        <Line
          points={[0, reorderDrag.pos, renderWidth, reorderDrag.pos]}
          stroke="#3B82F6"
          strokeWidth={2}
          dash={[6, 6]}
          listening={false}
        />
      )}
      {reorderDrag?.axis === 'col' && (
        <Line
          points={[reorderDrag.pos, 0, reorderDrag.pos, renderHeight]}
          stroke="#3B82F6"
          strokeWidth={2}
          dash={[6, 6]}
          listening={false}
        />
      )}
      {buildRowReorderHandles()}
      {buildColReorderHandles()}
      {buildRowHandles()}
      {buildColHandles()}
    </Group>
  );
});

TableShapeNode.displayName = 'TableShapeNode';
